import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import './styles.css';
import { getUrlHostname, getUrlWithProtocol } from '../../../utils/url';
import { getEnrollmentData } from '../../../utils/charts';
import { getDegreeOfferingList } from '../../../utils/degree-offerings';
import { ONLINE_ALL_PROGRAMS, ONLINE_SOME_PROGRAMS } from '../../../constants';
import SingleFact from './SingleFact';

const messages = defineMessages({
  quickFacts: {
    id: 'hubs.overview.school-profile.quick-facts',
    defaultMessage: 'Quick Facts',
  },
  website: {
    id: 'hubs.overview.school-profile.quick-facts.website',
    defaultMessage: 'Website',
  },
  schoolType: {
    id: 'hubs.overview.school-profile.quick-facts.schooltype',
    defaultMessage: 'School Type',
  },
  ugEnrollment: {
    id: 'hubs.overview.school-profile.quick-facts.ug-enrollment',
    defaultMessage: 'Undergraduate Enrollment',
  },
  ceebCode: {
    id: 'hubs.overview.school-profile.quick-facts.ceeb-code',
    defaultMessage: 'CEEB Code',
  },
  actCode: {
    id: 'hubs.overview.school-profile.quick-facts.act-code',
    defaultMessage: 'ACT Code',
  },
  studentFacultyRatio: {
    id: 'hubs.overview.school-profile.quick-facts.student-faculty-ratio',
    defaultMessage: 'Student-to-Faculty Ratio',
  },
  freshmanAcceptanceRate: {
    id: 'hubs.overview.school-profile.quick-facts.freshman-accept-rate',
    defaultMessage: 'Freshman Acceptance Rate',
  },
  religAffil: {
    id: 'hubs.overview.school-profile.quick-facts.religious-affiliation',
    defaultMessage: 'Religious Affiliation',
  },
  campusSurroundings: {
    id: 'hubs.overview.school-profile.quick-facts.campus-surroundings',
    defaultMessage: 'Campus Surroundings',
  },
  degreesOffered: {
    id: 'hubs.overview.school-profile.quick-facts.degrees-offered',
    defaultMessage: 'Degrees Offered',
  },
  avgClassSize: {
    id: 'hubs.overview.school-profile.quick-facts.avg-class-size',
    defaultMessage: 'Average Classroom Size',
  },
  onlinePrograms: {
    id: 'hubs.overview.school-profile.quick-facts.online-programs',
    defaultMessage: 'Online Programs',
  },
  jobPlacementRate: {
    id: 'hubs.overview.school-profile.quick-facts.job-placement-rate',
    defaultMessage: 'Job Placement Rate',
  },
});

const QuickFacts = ({ fullProfile, isCamino }) => {
  const websiteUrl = get(fullProfile, 'overview.website.value', '');
  const schoolLevel = get(fullProfile, 'overview.schoolType.level.value', '');
  const schoolType = get(fullProfile, 'overview.schoolType.control.value', '');
  const schoolSize = get(fullProfile, 'studentLife.schoolSize', {});
  const [undergradEnrollment] = getEnrollmentData(schoolSize);
  const studentToFacultyRatio = get(
    fullProfile,
    'overview.studentFacultyRatio.value',
    '',
  );
  const citySize = get(fullProfile, 'overview.campusSurroundings.citySize.value', '');
  const campusSurroundings = citySize.includes(',')
    ? citySize.substr(0, citySize.indexOf(','))
    : '';
  const actCode = get(fullProfile, 'overview.actCode.value', '');
  const religiousAffiliation = get(
    fullProfile,
    'overview.religiousAffiliation.value',
    '',
  );
  const navianceId = get(fullProfile, 'overview.navianceId.value', '');
  const avgClassSize = get(fullProfile, 'studies.avgClassSize.value', '');

  const degreeOfferingList = getDegreeOfferingList(
    get(fullProfile, 'studies.majors', []),
  );

  const programsOnline = get(fullProfile, 'studies.programsOnline.value', '');
  const onlineProgramsOffered =
    programsOnline === ONLINE_SOME_PROGRAMS || programsOnline === ONLINE_ALL_PROGRAMS
      ? 'Offered'
      : 'None';

  const jpLessThan1Year = get(
    fullProfile,
    'studies.jobPlacementRate.lessThan1Year.value',
    null,
  );
  const jpLessThan6Months = get(
    fullProfile,
    'studies.jobPlacementRate.lessThan6Months.value',
    null,
  );
  const acceptanceRateValue =
    isCamino &&
    Math.round(
      (+get(fullProfile, 'admissions.acceptanceRate.applicationsAccepted.value', 0) *
        100) /
        +get(fullProfile, 'admissions.acceptanceRate.applicationsReceived.value', 0),
    );

  return (
    <div className="quick-facts-container">
      <h3 data-cy="quick-facts-heading" className="quick-facts-heading">
        <FormattedMessage {...messages.quickFacts} />
      </h3>
      <div className="quick-facts-content">
        {websiteUrl && (
          <React.Fragment>
            <div>
              <FormattedMessage {...messages.website} />{' '}
            </div>
            <div>
              <a
                data-cy="qf-website-link"
                href={getUrlWithProtocol(websiteUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {getUrlHostname(websiteUrl)}
              </a>
            </div>
          </React.Fragment>
        )}

        <SingleFact
          message={messages.schoolType}
          value={
            schoolType
              ? `${schoolType.split(' ')[0]} ${schoolLevel ? `/ ${schoolLevel}` : ''}`
              : null
          }
          dataCy="qf-school-type"
        />

        <SingleFact
          message={messages.ceebCode}
          value={navianceId}
          dataCy="qf-ceeb-code"
        />

        <SingleFact message={messages.actCode} value={actCode} dataCy="qf-act-code" />

        <SingleFact
          message={messages.ugEnrollment}
          value={
            undergradEnrollment
              ? undergradEnrollment.toLocaleString('en-US', { minimumFractionDigits: 0 })
              : null
          }
          dataCy="qf-ug-enrollment"
        />

        <SingleFact
          message={messages.studentFacultyRatio}
          value={studentToFacultyRatio ? `${studentToFacultyRatio} to 1` : null}
          dataCy="qf-student-faculty-ratio"
        />

        <SingleFact
          message={messages.freshmanAcceptanceRate}
          value={acceptanceRateValue ? `${acceptanceRateValue}%` : null}
          dataCy="qf-freshman-accept-rate"
        />

        <SingleFact
          message={messages.avgClassSize}
          value={avgClassSize}
          dataCy="qf-avg-class-size"
        />

        {(jpLessThan1Year || jpLessThan6Months) && (
          <React.Fragment>
            <div>
              <FormattedMessage {...messages.jobPlacementRate} />
            </div>
            <div data-cy="qf-job-placement-rate" className="qf-job-placement-rate">
              {jpLessThan6Months && <div>{jpLessThan6Months}% within 6 Months</div>}
              {jpLessThan1Year && <div>{jpLessThan1Year}% within 1 Year</div>}
            </div>
          </React.Fragment>
        )}

        <SingleFact
          message={messages.campusSurroundings}
          value={campusSurroundings}
          dataCy="qf-campus-surroundings"
        />

        <SingleFact
          message={messages.religAffil}
          value={religiousAffiliation}
          dataCy="qf-religious-affiliation"
        />

        <SingleFact
          message={messages.onlinePrograms}
          value={onlineProgramsOffered}
          dataCy="qf-online-programs"
          id="qf-online-programs-id"
        />

        {degreeOfferingList && degreeOfferingList.length > 0 && (
          <React.Fragment>
            <div>
              <FormattedMessage {...messages.degreesOffered} />
            </div>
            <ul className="qf-degree-list">
              {degreeOfferingList.map((degree) => (
                <li data-cy="qf-degree-offered" key={degree}>
                  {degree}
                </li>
              ))}
            </ul>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

QuickFacts.propTypes = {
  fullProfile: PropTypes.object,
  isCamino: PropTypes.bool,
};

export default injectIntl(QuickFacts);
